import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { Batches } from '@pages/Batches';
import { BatchPayouts } from '@pages/BatchPayouts';
import { ImpersonationLogin } from '@pages/ImpersonationLogin';
import { PayoutDrafts } from '@pages/PayoutDrafts';
import { CreateManualPayout } from '@pages/Payouts/NewCreateManualPayout';
import { Preview } from '@pages/Preview';
import { Receivers } from '@pages/Receivers';
import { TOTPValidate } from '@pages/TOTPValidate';
import { AccountsRedirection } from 'containers/redirections/AccountsRedirection';
import { RedirectToDefaultOrganisation } from 'containers/redirections/DefaultOrganisationRedirection';
import { RequireAuth } from 'containers/RequireAuth';
import { RequireBatchesEnabled } from 'containers/RequireBatchesEnabled';
import { RequireConfiguration } from 'containers/RequireConfiguration';
import { RequireFeatureFlag } from 'containers/RequireFeatureFlag';
import { RequireOrganisation } from 'containers/RequireOrganisation';
import { RequirePermission } from 'containers/RequirePermission';

import { AccountTransactions } from 'components/pages/AccountTransactions';
import { CryptoFunding } from 'components/pages/CryptoFunding';
import { Dashboard } from 'components/pages/Dashboard';
import { Authentication } from 'components/pages/Developers/Authentication';
import { BusinessUnits } from 'components/pages/Developers/BusinessUnits';
import { Mcp } from 'components/pages/Developers/Mcp';
import { Webhooks } from 'components/pages/Developers/Webhooks';
import { Exports } from 'components/pages/ExportedStatements';
import { ExportedPayins } from 'components/pages/ExportedStatements/ExportedPayins';
import { ExportedPayouts } from 'components/pages/ExportedStatements/ExportedPayouts';
import { ForgotPassword } from 'components/pages/ForgotPassword';
import { InvitationAccept } from 'components/pages/InvitationAccept';
import { InvitationExpired } from 'components/pages/InvitationsExpired';
import { Login } from 'components/pages/Login';
import { LoginTransition } from 'components/pages/LoginTransition';
import { Logout } from 'components/pages/Logout';
import { MfaSetup } from 'components/pages/MfaSetup';
import { MfaVerify } from 'components/pages/MfaVerify';
import { NoOrganisation } from 'components/pages/NoOrganisation';
import { Orders } from 'components/pages/Orders';
import { OrganisationsLoadingTransition } from 'components/pages/OrganisationsLoadingTransition';
import { PageNotFound } from 'components/pages/PageNotFound';
import { Payouts } from 'components/pages/Payouts';
import { Search } from 'components/pages/Search';
import { UpdatePassword } from 'components/pages/UpdatePassword';
import { UserManagement } from 'components/pages/UserManagement';
import { UserProfile } from 'components/pages/UserProfile';
import { Withdrawals } from 'components/pages/Withdrawals';
import { AppLayout } from 'components/ui/templates/AppLayout';
import { AuthenticationLayout } from 'components/ui/templates/AuthenticationLayout';

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AuthenticationLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="impersonation_login" element={<ImpersonationLogin />} />
          <Route path="invitations">
            <Route path="expired" element={<InvitationExpired />} />
            <Route path="accept/:token" element={<InvitationAccept />} />
          </Route>
          <Route path="passwords">
            <Route path="forgot" element={<ForgotPassword />} />
            <Route path="reset/:token" element={<UpdatePassword />} />
          </Route>
          <Route path="logout" element={<Logout />} />
        </Route>

        <Route path="/no-organisation" element={<NoOrganisation />} />

        <Route
          element={
            <RequireAuth
              allowedScopes={['login', 'dashboard']}
              redirectionPath="/"
            />
          }
        >
          <Route path="mfa-verify" element={<MfaVerify />} />
          <Route path="mfa-setup" element={<MfaSetup />} />
          <Route path="totp-validate" element={<TOTPValidate />} />
        </Route>

        {/* routes with real data */}
        <Route element={<RequireAuth allowedScopes={['dashboard']} />}>
          {/* rendering transition page that loads the list of organisations*/}
          <Route
            path="/login_transition"
            element={<OrganisationsLoadingTransition />}
          />
          <Route path="preview/:previewMetadata" element={<Preview />} />
          {/* validation and setting current organisation */}
          <Route path="/org/:organisationId" element={<RequireOrganisation />}>
            <Route path="login_transition" element={<LoginTransition />} />

            <Route element={<AppLayout />}>
              {/* Dashboard */}
              <Route
                element={
                  <RequirePermission requiredPermission="dashboard:read" />
                }
              >
                <Route index element={<Dashboard />} />
              </Route>

              {/* Search */}
              <Route path="search/*" element={<Search />} />

              {/* Payouts */}
              <Route
                element={
                  <RequirePermission requiredPermission="payouts:read" />
                }
              >
                <Route path="payouts">
                  <Route index element={<Payouts />} />

                  <Route
                    element={
                      <RequireConfiguration requiredConfiguration="isCustomerABTestingEnabled" />
                    }
                  >
                    <Route
                      element={
                        <RequirePermission requiredPermission="payouts:manage" />
                      }
                    >
                      <Route path="create" element={<CreateManualPayout />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Crypto funding */}
              <Route
                element={
                  <RequirePermission requiredPermission="crypto:manage" />
                }
              >
                <Route path="crypto_funding/*" element={<CryptoFunding />} />
              </Route>

              {/* Payout Batches */}
              <Route
                element={
                  <RequireFeatureFlag featureFlag="CBP-1761-customer-app-batches" />
                }
              >
                <Route
                  element={
                    <RequirePermission requiredPermission="payout_batches:read" />
                  }
                >
                  <Route element={<RequireBatchesEnabled />}>
                    <Route path="batches/*" element={<Batches />} />
                    <Route
                      path="batches/:batchId/batch_payouts/*"
                      element={<BatchPayouts />}
                    />
                  </Route>
                </Route>
              </Route>

              {/* Payout Drafts */}
              <Route
                element={
                  <RequirePermission requiredPermission="payout_drafts:read" />
                }
              >
                <Route
                  element={
                    <RequireConfiguration requiredConfiguration="isPayoutDraftApprovalFlowEnabled" />
                  }
                >
                  <Route path="payout_drafts/*" element={<PayoutDrafts />} />
                </Route>
              </Route>

              {/* Receivers */}
              <Route
                element={
                  <RequirePermission requiredPermission="payouts:read" />
                }
              >
                <Route
                  element={
                    <RequireFeatureFlag featureFlag="CBP-8000-receiver-management" />
                  }
                >
                  <Route path="receivers/*" element={<Receivers />} />
                </Route>
              </Route>

              {/* Orders */}
              <Route
                path="collections/orders"
                element={
                  <RequirePermission requiredPermission="collection_orders:read" />
                }
              >
                <Route path=":orderId" element={<Orders />} />
                <Route path="" element={<Orders />} />
              </Route>

              {/* Accounts / Exports / Withdrawals */}
              <Route
                element={
                  <RequirePermission requiredPermission="accounts:read" />
                }
              >
                <Route
                  element={
                    <RequirePermission requiredPermission="exports:read" />
                  }
                >
                  <Route path="exports" element={<Exports />} />
                  <Route path="exported_statements" element={<Exports />} />
                  <Route
                    path="exported_payouts"
                    element={<ExportedPayouts />}
                  />
                  <Route path="exported_payins" element={<ExportedPayins />} />
                  {/* Alias: the PEGO-14838 report-ready email deep-links to
                      /exported_orders?download_orders=<uuid> */}
                  <Route path="exported_orders" element={<ExportedPayins />} />
                </Route>
                <Route
                  element={
                    <RequirePermission requiredPermission="withdrawals:read" />
                  }
                >
                  <Route
                    path="accounts/withdrawals/*"
                    element={<Withdrawals />}
                  />
                </Route>
                <Route
                  path="accounts/:virtual_account_number/*"
                  element={<AccountTransactions />}
                />
                <Route path="accounts" element={<AccountsRedirection />} />
              </Route>

              {/* Settings / Users */}
              <>
                <Route path="settings/profile" element={<UserProfile />} />
                <Route
                  element={
                    <RequirePermission requiredPermission="users:read" />
                  }
                >
                  <Route path="users/*" element={<UserManagement />} />
                </Route>
              </>

              {/* Developers */}
              <Route
                element={
                  <RequireFeatureFlag featureFlag="PEGO-13634-self-service-developer-settings" />
                }
              >
                <Route
                  element={
                    <RequirePermission requiredPermission="developers:manage" />
                  }
                >
                  <Route path="developers">
                    <Route path="authentication" element={<Authentication />} />
                    <Route path="webhooks" element={<Webhooks />} />
                    <Route
                      element={
                        <RequireFeatureFlag featureFlag="PEGO-14432-mcp-tokens" />
                      }
                    >
                      <Route path="mcp" element={<Mcp />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Configurations */}
              <Route
                element={
                  <RequireFeatureFlag featureFlag="PEGO-13634-self-service-developer-settings" />
                }
              >
                <Route
                  element={
                    <RequirePermission requiredPermission="configurations:read" />
                  }
                >
                  <Route path="configurations">
                    <Route path="business_units" element={<BusinessUnits />} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<PageNotFound />} />
            </Route>
          </Route>
          {/* redirect empty root url to default organisation */}
          <Route path="/" element={<RedirectToDefaultOrganisation />} />
        </Route>

        {/* catch unauthorized root url */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
